<script setup>
import { computed, onMounted, ref } from 'vue';
import { useHead, useSeoMeta } from '@unhead/vue';
import CompositeMarkdown from '@/components/composite/CompositeMarkdown.vue';
import GradientScoreBar from '@/components/GradientScoreBar.vue';
import LandingNav from '@/components/landing/LandingNav.vue';
import {
  compositeAbout,
  compositeCitation,
  compositeContact,
  compositeContributors,
  compositeExampleTasks,
  compositeLinks,
  compositeOrganizers,
  compositeSummary,
  domainBreakdown,
  passAt1,
} from '@/data/composite-stem';
import orbAvatar from '@/assets/images/avatar/orb_avatar.png';
import arxivLogo from '@/assets/images/brands/arxiv-logo.svg';
import githubMark from '@/assets/images/brands/github-mark.svg';
import huggingfaceLogo from '@/assets/images/brands/huggingface-logo.svg';
import { fetchLlms } from '@/services/leaderboard-service';

const currentDomain = 'https://portexai.com';
const canonicalUrl = `${currentDomain}/composite`;
const compositeOgImage = `${currentDomain}/images/og-composite-stem.png`;
const pageDescription =
  '70 expert-curated agentic tasks across Physics, Biology, Chemistry, and Math, compatible with the Harbor Framework.';

useHead({
  title: 'COMPOSITE-STEM',
  link: [
    { rel: 'canonical', href: canonicalUrl },
  ],
  meta: [
    { property: 'og:image', content: compositeOgImage },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: 'COMPOSITE-STEM benchmark preview card' },
    { name: 'twitter:image', content: compositeOgImage },
    { name: 'twitter:image:alt', content: 'COMPOSITE-STEM benchmark preview card' },
  ],
});

useSeoMeta({
  title: 'COMPOSITE-STEM',
  titleTemplate: '%s - Portex AI',
  description: pageDescription,
  ogTitle: 'COMPOSITE-STEM',
  ogDescription: pageDescription,
  ogUrl: canonicalUrl,
  ogType: 'website',
  ogImage: compositeOgImage,
  twitterCard: 'summary_large_image',
  twitterTitle: 'COMPOSITE-STEM',
  twitterDescription: pageDescription,
  twitterImage: compositeOgImage,
});

const llmIconByFamily = ref({});
const selectedExampleSlug = ref(compositeExampleTasks[0]?.slug || '');
const activeExamplePanel = ref('task');
const showAllContributors = ref(false);
const showFullInstruction = ref(false);
const showFullDockerfile = ref(false);
const citationCopied = ref(false);

const selectedExample = computed(() => {
  return compositeExampleTasks.find((item) => item.slug === selectedExampleSlug.value) || compositeExampleTasks[0];
});

const sampleRun = computed(() => selectedExample.value?.sampleRun || null);

const statCards = [
  { label: 'Tasks', value: compositeSummary.tasks },
  { label: 'Contributors', value: compositeSummary.contributors },
  { label: 'Domains', value: compositeSummary.domains },
];

const domainIconMap = {
  Physics: 'mdi mdi-atom',
  Biology: 'mdi mdi-dna',
  Chemistry: 'mdi mdi-flask-outline',
  Math: 'mdi mdi-function-variant',
};

const primaryLinks = [
  {
    label: 'Paper',
    logo: arxivLogo,
    logoClass: 'paper-button__brand-image paper-button__brand-image--arxiv',
    url: compositeLinks.arxivAbstract,
  },
  {
    label: 'Dataset',
    logo: huggingfaceLogo,
    logoClass: 'paper-button__brand-image paper-button__brand-image--huggingface',
    url: compositeLinks.datasetRepo,
  },
  {
    label: 'Harbor Adapter',
    logo: githubMark,
    logoClass: 'paper-button__brand-image paper-button__brand-image--github',
    url: compositeLinks.harborAdapter,
  },
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const familyMatchers = [
  {
    family: 'gemini',
    matches: ['gemini', 'google'],
  },
  {
    family: 'claude',
    matches: ['claude', 'anthropic'],
  },
  {
    family: 'gpt',
    matches: ['gpt', 'openai'],
  },
  {
    family: 'grok',
    matches: ['grok', 'xai', 'x.ai'],
  },
];

const buildFamilyIconMap = (llms) => {
  return (Array.isArray(llms) ? llms : []).reduce((acc, llm) => {
    const name = normalizeText(llm?.name);
    const icon = llm?.icon || null;

    if (!name || !icon) {
      return acc;
    }

    acc[name] = icon;

    familyMatchers.forEach(({ family, matches }) => {
      if (!acc[family] && matches.some((token) => name.includes(token))) {
        acc[family] = icon;
      }
    });

    return acc;
  }, {});
};

const resolveModelIcon = (modelName, iconMap) => {
  const normalizedName = normalizeText(modelName);
  if (!normalizedName) return null;

  if (iconMap[normalizedName]) {
    return iconMap[normalizedName];
  }

  const familyMatch = familyMatchers.find(({ matches }) => {
    return matches.some((token) => normalizedName.includes(token));
  });

  return familyMatch ? iconMap[familyMatch.family] || null : null;
};

const leaderboardItems = computed(() => {
  return [...passAt1]
    .map((item) => ({
      ...item,
      llm_icon: resolveModelIcon(item.llm, llmIconByFamily.value),
    }))
    .sort((a, b) => b.score - a.score);
});

const domainCards = computed(() => {
  return domainBreakdown.map((item) => ({
    ...item,
    icon: domainIconMap[item.domain] || 'mdi-shape-outline',
    share: `${((item.count / compositeSummary.tasks) * 100).toFixed(1)}%`,
  }));
});

const contributorRows = computed(() => [...compositeOrganizers, ...compositeContributors]);

const visibleContributors = computed(() => {
  return showAllContributors.value ? contributorRows.value : contributorRows.value.slice(0, 6);
});

const formatPercent = (value) => `${Number(value).toFixed(1)}%`;

const getModelInitial = (modelName) => {
  if (!modelName) return '?';
  return String(modelName).trim().charAt(0).toUpperCase();
};

const formatModelLabel = (value) => {
  if (!value) return '';
  const parts = String(value).split('/');
  return parts[parts.length - 1] || value;
};

const sampleRunHighlights = computed(() => {
  if (!sampleRun.value) return [];

  return [
    {
      label: 'Model',
      value: formatModelLabel(sampleRun.value.agent.model),
      detail: `${sampleRun.value.agent.name} v${sampleRun.value.agent.version}`,
    },
    {
      label: 'Outcome',
      value: sampleRun.value.outcome.status,
      detail: `Grade ${sampleRun.value.outcome.grade}`,
    },
    {
      label: 'Score',
      value: `${sampleRun.value.outcome.scorePercent}/100`,
      detail: `Reward ${sampleRun.value.outcome.reward}`,
    },
    {
      label: 'Final Answer',
      value: sampleRun.value.outcome.finalAnswer,
      detail: `Expected ${sampleRun.value.outcome.expectedAnswer}`,
    },
  ];
});

const selectExample = (slug) => {
  selectedExampleSlug.value = slug;
  activeExamplePanel.value = 'task';
  showFullInstruction.value = false;
  showFullDockerfile.value = false;
};

const copyCitation = async () => {
  try {
    await navigator.clipboard.writeText(compositeCitation.bibtex);
    citationCopied.value = true;
    window.setTimeout(() => {
      citationCopied.value = false;
    }, 2000);
  } catch (error) {
    console.error('Unable to copy COMPOSITE citation:', error);
  }
};

onMounted(async () => {
  try {
    const llms = await fetchLlms();
    llmIconByFamily.value = buildFamilyIconMap(llms);
  } catch (error) {
    console.error('Unable to load model family icons for COMPOSITE-STEM:', error);
  }
});
</script>

<template>
  <div class="min-h-screen bg-black">
    <LandingNav />

    <main class="px-4 pb-12 pt-24 sm:px-6 lg:px-8">
      <section class="mx-auto max-w-6xl">
        <div class="paper-shell rounded-[28px] px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
          <div class="hero-glow" aria-hidden="true"></div>

          <div class="flex justify-center">
            <img :src="orbAvatar" alt="Portex Orb" class="h-16 w-16 rounded-full object-cover sm:h-20 sm:w-20" />
          </div>

          <div class="mx-auto mt-4 max-w-3xl text-center">
            <h1 class="flex items-center justify-center bg-gradient-to-br from-white to-white/[0.493] bg-clip-text text-3xl font-semibold uppercase tracking-widest text-transparent sm:text-4xl">
              {{ compositeSummary.title }}
            </h1>
            <p class="mt-3 text-sm text-white/85 sm:text-base">
              {{ compositeSummary.subtitle }}
              <a
                :href="compositeSummary.harborUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="font-medium text-[#ff6b00] underline decoration-[#ff6b00]/40 underline-offset-4 transition-colors duration-200 hover:text-white"
              >
                {{ compositeSummary.harborLabel }}
              </a>.
            </p>
          </div>

          <div class="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              v-for="link in primaryLinks"
              :key="link.label"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="paper-button"
            >
              <img
                :src="link.logo"
                alt=""
                aria-hidden="true"
                :class="link.logoClass"
              />
              <span>{{ link.label }}</span>
            </a>
          </div>

          <div class="mx-auto mt-8 max-w-4xl rounded-[22px] border border-[#0F0F0F] bg-[rgba(5,5,5,0.72)] px-5 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] sm:px-6 sm:py-6">
            <div class="mx-auto max-w-3xl md:grid md:grid-cols-[110px_minmax(0,1fr)] md:gap-6">
              <div class="text-center md:pt-1 md:text-left">
                <div class="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#ff8a66]">
                  {{ compositeAbout.title }}
                </div>
              </div>
              <div class="mt-4 space-y-4 text-center md:mt-0 md:text-left">
                <p
                  v-for="(paragraph, index) in compositeAbout.paragraphs"
                  :key="paragraph"
                  :class="index === 0 ? 'text-[15px] leading-7 text-[#ddd4ce]' : 'text-sm leading-7 text-[#c1b8b1]'"
                >
                  {{ paragraph }}
                </p>
              </div>
            </div>
          </div>

          <div class="mt-10 space-y-4">
            <div class="sketch-card rounded-[22px] px-5 py-5 sm:px-6 sm:py-6">
              <div class="text-sm font-semibold text-white sm:text-base">Pass@1 Leaderboard</div>
              <div class="mt-6 space-y-4">
                <div class="space-y-3 sm:hidden">
                  <div
                    v-for="(item, index) in leaderboardItems"
                    :key="`${item.llm}-mobile`"
                    class="rounded-[18px] border border-[#141414] bg-[#090909] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex min-w-0 items-center gap-3">
                        <div
                          v-if="item.llm_icon"
                          class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#0F0F0F]"
                        >
                          <img :src="item.llm_icon" :alt="`${item.llm} logo`" class="h-full w-full object-cover" />
                        </div>
                        <div
                          v-else
                          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#0F0F0F] text-xs font-semibold text-white/70"
                        >
                          {{ getModelInitial(item.llm) }}
                        </div>
                        <div class="min-w-0">
                          <div class="text-[10px] uppercase tracking-[0.18em] text-white/35">Rank {{ index + 1 }}</div>
                          <div class="mt-1 truncate text-sm font-medium leading-tight text-white/90">{{ item.llm }}</div>
                        </div>
                      </div>
                      <div class="shrink-0 pt-0.5 text-sm font-medium text-white/80">{{ formatPercent(item.score) }}</div>
                    </div>

                    <div class="mt-3">
                      <GradientScoreBar
                        :value="item.score"
                        orientation="horizontal"
                        length="w-full"
                        thickness="h-4"
                        rounded-size="rounded-full"
                        background="bg-[#0F0F0F]"
                      />
                    </div>
                  </div>
                </div>

                <div class="hidden gap-3 text-[11px] uppercase tracking-[0.15em] text-white/40 sm:grid sm:grid-cols-[48px_minmax(0,1fr)_minmax(260px,420px)] lg:grid-cols-[56px_minmax(0,1fr)_minmax(360px,520px)] xl:grid-cols-[56px_minmax(0,1fr)_minmax(420px,600px)]">
                  <div>Rank</div>
                  <div>Model</div>
                  <div>Pass@1</div>
                </div>

                <div
                  v-for="(item, index) in leaderboardItems"
                  :key="item.llm"
                  class="hidden text-sm text-white/90 sm:grid sm:grid-cols-[48px_minmax(0,1fr)_minmax(260px,420px)] sm:items-center sm:gap-3 lg:grid-cols-[56px_minmax(0,1fr)_minmax(360px,520px)] xl:grid-cols-[56px_minmax(0,1fr)_minmax(420px,600px)]"
                >
                  <div>{{ index + 1 }}</div>
                  <div class="min-w-0 contents">
                    <div class="flex min-w-0 items-center gap-3">
                      <div
                        v-if="item.llm_icon"
                        class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#0F0F0F]"
                      >
                        <img :src="item.llm_icon" :alt="`${item.llm} logo`" class="h-full w-full object-cover" />
                      </div>
                      <div
                        v-else
                        class="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-[#0F0F0F] text-xs font-semibold text-white/70"
                      >
                        {{ getModelInitial(item.llm) }}
                      </div>
                      <span class="min-w-0 truncate text-sm text-white/90">{{ item.llm }}</span>
                    </div>
                    <div class="flex min-w-0 items-center gap-3">
                      <div class="min-w-0 flex-1">
                        <GradientScoreBar
                          :value="item.score"
                          orientation="horizontal"
                          length="w-full sm:w-56 lg:w-80 xl:w-[30rem]"
                          thickness="h-6"
                          rounded-size="rounded-full"
                          background="bg-[#0F0F0F]"
                        />
                      </div>
                      <span class="min-w-[48px] text-right text-sm font-medium">{{ formatPercent(item.score) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div
                v-for="item in domainCards"
                :key="item.domain"
                class="domain-chip"
              >
                <div class="domain-chip__left">
                  <div class="domain-chip__icon">
                    <i :class="[item.icon, 'text-xl']"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="text-sm font-semibold text-white">{{ item.domain }}</div>
                    <div class="text-xs text-white/40">Domain</div>
                  </div>
                </div>
                <div class="shrink-0 text-right">
                  <div class="text-base font-semibold text-white">{{ item.count }}</div>
                  <div class="text-xs text-white/45">{{ item.share }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
            <div v-for="stat in statCards" :key="stat.label" class="text-center">
              <div class="text-[11px] uppercase tracking-[0.22em] text-white/40">{{ stat.label }}</div>
              <div class="mt-2 text-4xl font-semibold leading-none text-white sm:text-5xl">{{ stat.value }}</div>
            </div>
          </div>

          <div class="mt-8 sketch-card rounded-[22px] px-5 py-5">
            <div class="text-center">
              <div class="text-sm font-semibold text-white">Contributors &amp; Organizing Team</div>
              <p class="mt-2 text-xs text-white/45">
                Contributors across Physics, Biology, Chemistry, Math, and the Portex organizing team.
              </p>
            </div>

            <div class="mt-5 overflow-x-auto">
              <table class="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr class="border-b border-[#0F0F0F] text-left text-[11px] uppercase tracking-[0.18em] text-white/40">
                    <th class="px-3 py-2 font-medium">Name</th>
                    <th class="px-3 py-2 font-medium">Affiliation</th>
                    <th class="px-3 py-2 font-medium">Role</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="contributor in visibleContributors"
                    :key="`${contributor.name}-${contributor.role}`"
                    class="border-b border-[#0F0F0F] text-sm text-white/90 last:border-b-0"
                  >
                    <td class="px-3 py-3">{{ contributor.name }}</td>
                    <td class="px-3 py-3">
                      <div class="flex items-center gap-3">
                        <div
                          class="contributor-logo-shell"
                          :class="{ 'contributor-logo-shell--empty': !contributor.logo && contributor.affiliation !== 'Independent' }"
                        >
                          <img
                            v-if="contributor.logo"
                            :src="contributor.logo"
                            :alt="`${contributor.affiliation} logo`"
                            class="contributor-logo-image"
                            loading="lazy"
                          />
                          <i
                            v-else-if="contributor.affiliation === 'Independent'"
                            class="mdi mdi-web contributor-logo-icon"
                            aria-hidden="true"
                          ></i>
                        </div>
                        <span>{{ contributor.affiliation }}</span>
                      </div>
                    </td>
                    <td class="px-3 py-3">{{ contributor.role }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-4 flex justify-center">
              <button type="button" class="see-more-button" @click="showAllContributors = !showAllContributors">
                <span>{{ showAllContributors ? 'Show Fewer Contributors' : 'See All Contributors' }}</span>
                <i :class="[showAllContributors ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down', 'text-lg']"></i>
              </button>
            </div>
          </div>

          <div class="mt-8 sketch-card rounded-[22px] px-5 py-5">
            <div class="mb-5">
              <div class="text-[11px] uppercase tracking-[0.2em] text-white/40">Representative tasks</div>
              <div class="mt-3 flex flex-wrap gap-2.5">
                <button
                  v-for="example in compositeExampleTasks"
                  :key="example.slug"
                  type="button"
                  class="example-pill"
                  :class="{ 'example-pill--active': selectedExample.slug === example.slug }"
                  @click="selectExample(example.slug)"
                >
                  <span class="example-pill__domain">{{ example.domain }}</span>
                  <span class="example-pill__title">{{ example.title }}</span>
                </button>
              </div>
            </div>

            <div class="flex flex-wrap items-center justify-between gap-3">
              <span class="inline-flex rounded-full border border-white/10 bg-[#0F0F0F] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                {{ selectedExample.domain }}
              </span>
              <div class="text-right">
                <div class="text-sm font-semibold text-white">{{ selectedExample.title }}</div>
                <div class="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/40">
                  {{ selectedExample.taskId }}
                </div>
              </div>
            </div>

            <div class="mt-4 inline-flex rounded-full border border-white/10 bg-[#0F0F0F] p-1">
              <button
                type="button"
                class="tab-button"
                :class="{ 'tab-button--active': activeExamplePanel === 'task' }"
                @click="activeExamplePanel = 'task'"
              >
                Task
              </button>
              <button
                type="button"
                class="tab-button"
                :class="{ 'tab-button--active': activeExamplePanel === 'answer' }"
                @click="activeExamplePanel = 'answer'"
              >
                Answer
              </button>
              <button
                v-if="sampleRun"
                type="button"
                class="tab-button"
                :class="{ 'tab-button--active': activeExamplePanel === 'run' }"
                @click="activeExamplePanel = 'run'"
              >
                Trajectory
              </button>
            </div>

            <div v-if="activeExamplePanel === 'task'" class="mt-5">
              <div class="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.05fr)_minmax(300px,0.95fr)]">
                <div class="space-y-4">
                  <p class="text-sm leading-7 text-white/75">
                    {{ selectedExample.taskText }}
                  </p>

                  <div class="rounded-[18px] border border-[#FE3E01]/12 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex items-center justify-between gap-3">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-[#ff8a66]">Raw instruction.md</div>
                      <button
                        type="button"
                        class="see-more-button text-sm"
                        @click="showFullInstruction = !showFullInstruction"
                      >
                        <span>{{ showFullInstruction ? 'Collapse' : 'Expand' }}</span>
                        <i :class="[showFullInstruction ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down', 'text-lg']"></i>
                      </button>
                    </div>

                    <div
                      class="instruction-shell mt-4"
                      :class="{ 'instruction-shell--collapsed': !showFullInstruction }"
                    >
                      <CompositeMarkdown :content="selectedExample.rawInstruction" />
                      <div v-if="!showFullInstruction" class="instruction-fade" aria-hidden="true"></div>
                    </div>
                  </div>

                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">Base image</div>
                      <div class="mt-2 text-sm text-white/82">{{ selectedExample.environment.baseImage }}</div>
                    </div>
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">Working directory</div>
                      <div class="mt-2 text-sm text-white/82">{{ selectedExample.environment.workdir }}</div>
                    </div>
                  </div>

                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">Reference assets</div>
                      <div class="mt-2 break-all text-sm text-white/82">{{ selectedExample.environment.copiedAssets }}</div>
                    </div>
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">Submission artifact</div>
                      <div class="mt-2 break-all text-sm text-white/82">{{ selectedExample.submissionPath }}</div>
                    </div>
                  </div>

                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">System tools</div>
                      <div class="mt-2 space-y-1">
                        <div
                          v-for="pkg in selectedExample.environment.systemPackages"
                          :key="pkg"
                          class="text-sm text-white/72"
                        >
                          {{ pkg }}
                        </div>
                      </div>
                    </div>
                    <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">Python packages</div>
                      <div class="mt-2 flex flex-wrap gap-2">
                        <span
                          v-for="pkg in selectedExample.environment.pythonPackages"
                          :key="pkg"
                          class="inline-flex rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-xs text-white/65"
                        >
                          {{ pkg }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex items-center justify-between gap-3">
                      <div class="text-[11px] uppercase tracking-[0.18em] text-[#ff8a66]">Dockerfile</div>
                      <button
                        type="button"
                        class="see-more-button text-sm"
                        @click="showFullDockerfile = !showFullDockerfile"
                      >
                        <span>{{ showFullDockerfile ? 'Collapse' : 'Expand' }}</span>
                        <i :class="[showFullDockerfile ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down', 'text-lg']"></i>
                      </button>
                    </div>

                    <div
                      class="instruction-shell mt-4"
                      :class="{ 'instruction-shell--collapsed': !showFullDockerfile }"
                    >
                      <pre class="instruction-pre">{{ selectedExample.environment.dockerfile }}</pre>
                      <div v-if="!showFullDockerfile" class="instruction-fade" aria-hidden="true"></div>
                    </div>
                  </div>

                </div>

                <div
                  class="rounded-[18px] border border-white/10 bg-[#0F0F0F] p-3"
                  :class="{ 'flex min-h-[20rem] items-center justify-center': !selectedExample.imageSrc }"
                >
                  <template v-if="selectedExample.imageSrc">
                    <img
                      :src="selectedExample.imageSrc"
                      :alt="selectedExample.imageAlt"
                      class="w-full rounded-[14px] object-cover"
                    />
                    <p class="mt-3 text-xs leading-6 text-white/45">
                      {{ selectedExample.imageCaption }}
                    </p>
                  </template>
                  <div v-else class="rounded-[16px] border border-dashed border-white/10 bg-black/30 px-6 py-8 text-center">
                    <div class="text-sm font-semibold text-white">No reference file required</div>
                    <p class="mt-3 max-w-sm text-sm leading-7 text-white/55">
                      This example is a reasoning-heavy math task. The bundle includes the same Harbor environment scaffold, but the agent works directly from the written prompt instead of an attached artifact.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeExamplePanel === 'answer'" class="mt-5 space-y-4">
              <p class="max-w-2xl text-sm leading-7 text-white/75">
                {{ selectedExample.answerText }}
              </p>

              <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4 text-sm text-white/70">
                Required artifact:
                <code class="rounded bg-black/40 px-1.5 py-0.5 text-xs text-[#ff8a66]">
                  {{ selectedExample.submissionPath }}
                </code>
              </div>

              <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                <div class="flex items-center justify-between gap-3">
                  <div class="text-sm font-semibold text-white">Grading rubric</div>
                  <span class="text-[11px] uppercase tracking-[0.18em] text-white/40">criteria.json</span>
                </div>

                <div class="mt-4 space-y-3">
                  <div
                    v-for="criterion in selectedExample.rubric"
                    :key="criterion.name"
                    class="rounded-[16px] border border-[#FE3E01]/12 bg-[rgba(254,62,1,0.04)] px-4 py-4"
                  >
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <div class="text-sm font-medium text-white">{{ criterion.name }}</div>
                      <div class="flex items-center gap-2">
                        <span class="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#ffb18c]">
                          {{ criterion.evaluator }}
                        </span>
                        <span class="text-xs font-medium text-white/55">{{ criterion.weight }}%</span>
                      </div>
                    </div>
                    <p class="mt-3 text-sm leading-7 text-[#d6cdc6]">
                      {{ criterion.criterion }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeExamplePanel === 'run'" class="mt-5 space-y-4">
              <p class="max-w-3xl text-sm leading-7 text-white/75">
                {{ sampleRun.note }}
              </p>

              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div
                  v-for="item in sampleRunHighlights"
                  :key="item.label"
                  class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4"
                >
                  <div class="text-[11px] uppercase tracking-[0.18em] text-white/40">{{ item.label }}</div>
                  <div class="mt-2 text-lg font-semibold text-white">{{ item.value }}</div>
                  <div class="mt-1 text-xs text-[#c1b8b1]">{{ item.detail }}</div>
                </div>
              </div>

              <div class="grid gap-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(320px,1.08fr)]">
                <div class="space-y-4">
                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <div class="text-sm font-semibold text-white">Agent trajectory</div>
                      <span class="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/45">
                        Session {{ sampleRun.agent.sessionId.slice(0, 8) }}
                      </span>
                    </div>

                    <div class="mt-4 space-y-3">
                      <div
                        v-for="step in sampleRun.timeline"
                        :key="step.step"
                        class="trajectory-step"
                      >
                        <div class="trajectory-step__index">{{ step.step }}</div>
                        <div class="min-w-0 flex-1">
                          <div class="flex flex-wrap items-center justify-between gap-2">
                            <div class="text-sm font-medium text-white">{{ step.title }}</div>
                            <span class="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-[#ffb18c]">
                              {{ step.source }}
                            </span>
                          </div>
                          <p class="mt-2 text-sm leading-7 text-[#d6cdc6]">
                            {{ step.detail }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="text-sm font-semibold text-white">Agent configuration</div>
                    <div class="mt-4 grid gap-3 sm:grid-cols-2">
                      <div class="run-config-card">
                        <div class="run-config-card__label">Agent</div>
                        <div class="run-config-card__value">{{ sampleRun.agent.name }}</div>
                      </div>
                      <div class="run-config-card">
                        <div class="run-config-card__label">Raw model ID</div>
                        <div class="run-config-card__value break-all">{{ sampleRun.agent.model }}</div>
                      </div>
                      <div class="run-config-card">
                        <div class="run-config-card__label">Parser</div>
                        <div class="run-config-card__value">{{ sampleRun.agent.parser }}</div>
                      </div>
                      <div class="run-config-card">
                        <div class="run-config-card__label">Temperature</div>
                        <div class="run-config-card__value">{{ sampleRun.agent.temperature }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <div class="text-sm font-semibold text-white">Run result</div>
                      <span
                        class="run-status-chip"
                        :class="sampleRun.outcome.statusTone === 'failed' ? 'run-status-chip--failed' : 'run-status-chip--passed'"
                      >
                        {{ sampleRun.outcome.status }}
                      </span>
                    </div>

                    <p class="mt-3 text-sm leading-7 text-[#d6cdc6]">
                      {{ sampleRun.outcome.summary }}
                    </p>

                    <div class="mt-4 rounded-[16px] border border-[#FE3E01]/12 bg-[rgba(254,62,1,0.04)] px-4 py-4">
                      <div class="flex flex-wrap items-center gap-4 text-sm text-white/75">
                        <span>
                          Expected answer:
                          <code class="rounded bg-black/40 px-1.5 py-0.5 text-xs text-[#ff8a66]">
                            {{ sampleRun.outcome.expectedAnswer }}
                          </code>
                        </span>
                        <span>
                          Submitted:
                          <code class="rounded bg-black/40 px-1.5 py-0.5 text-xs text-[#ff8a66]">
                            {{ sampleRun.outcome.finalAnswer }}
                          </code>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex items-center justify-between gap-3">
                      <div class="text-sm font-semibold text-white">Sample submission</div>
                      <span class="text-[11px] uppercase tracking-[0.18em] text-white/40">answer.txt</span>
                    </div>
                    <div class="mt-4 rounded-[16px] border border-[#FE3E01]/12 bg-[rgba(254,62,1,0.04)] px-4 py-4">
                      <pre class="instruction-pre">{{ sampleRun.submissionPreview }}</pre>
                    </div>
                  </div>

                  <div class="rounded-[18px] border border-white/10 bg-[#0F0F0F] px-4 py-4">
                    <div class="flex items-center justify-between gap-3">
                      <div class="text-sm font-semibold text-white">Verifier outcome</div>
                      <span class="text-[11px] uppercase tracking-[0.18em] text-white/40">portex_detail.json</span>
                    </div>

                    <div class="mt-4 space-y-3">
                      <div
                        v-for="criterion in sampleRun.criteriaResults"
                        :key="criterion.name"
                        class="run-criterion"
                        :class="criterion.passed ? 'run-criterion--passed' : 'run-criterion--failed'"
                      >
                        <div class="flex flex-wrap items-center justify-between gap-3">
                          <div class="text-sm font-medium text-white">{{ criterion.name }}</div>
                          <div class="flex items-center gap-2">
                            <span
                              class="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]"
                              :class="criterion.passed ? 'bg-emerald-500/12 text-emerald-300' : 'bg-[#FE3E01]/12 text-[#ffb18c]'"
                            >
                              {{ criterion.passed ? 'Passed' : 'Failed' }}
                            </span>
                            <span class="text-xs font-medium text-white/55">{{ criterion.weight }}%</span>
                          </div>
                        </div>
                        <p class="mt-2 text-sm leading-7 text-[#d6cdc6]">
                          {{ criterion.explanation }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-10">
            <div class="text-center">
              <h2 class="text-2xl font-semibold text-white sm:text-3xl">Citation</h2>
              <div class="mx-auto mt-3 h-px w-20 bg-white/10"></div>
            </div>

            <div class="citation-card mt-6 rounded-[22px] px-4 py-4 sm:px-5 sm:py-5">
              <div class="flex justify-end">
                <button
                  type="button"
                  class="citation-copy-button"
                  :aria-label="citationCopied ? 'Citation copied' : 'Copy citation'"
                  @click="copyCitation"
                >
                  <i :class="[citationCopied ? 'mdi mdi-check' : 'mdi mdi-content-copy', 'text-[1.15rem]']"></i>
                </button>
              </div>

              <div class="citation-code-shell -mt-2 rounded-[18px] px-4 py-4 sm:px-5">
                <pre class="citation-pre">{{ compositeCitation.bibtex }}</pre>
              </div>
            </div>
          </div>

          <div class="mt-8 text-center text-sm text-white/45">
            Contact:
            <a
              :href="`mailto:${compositeContact.email}`"
              class="accent-link ml-1 underline decoration-white/20 underline-offset-4 transition-colors duration-200"
            >
              {{ compositeContact.email }}
            </a>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
.paper-shell {
  position: relative;
  overflow: hidden;
  border: 1px solid #0f0f0f;
  background: rgba(3, 3, 3, 0.96);
  box-shadow:
    0 26px 90px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.hero-glow {
  position: absolute;
  top: -9rem;
  left: 50%;
  width: 28rem;
  height: 18rem;
  transform: translateX(-50%);
  border-radius: 9999px;
  background:
    radial-gradient(circle, rgba(254, 62, 1, 0.14) 0%, rgba(254, 62, 1, 0.08) 32%, rgba(254, 62, 1, 0) 72%);
  filter: blur(18px);
  pointer-events: none;
}

.sketch-card {
  border: 1px solid #0f0f0f;
  background: rgba(5, 5, 5, 0.72);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.domain-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
  width: 100%;
  border: 1px solid rgba(254, 62, 1, 0.14);
  border-radius: 1.25rem;
  background:
    linear-gradient(135deg, rgba(254, 62, 1, 0.09) 0%, rgba(18, 9, 6, 0.7) 26%, rgba(5, 5, 5, 0.82) 62%),
    rgba(5, 5, 5, 0.82);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  padding: 0.9rem 1rem;
}

.domain-chip__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.domain-chip__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  background: rgba(254, 62, 1, 0.12);
  color: #fe3e01;
  border: 1px solid rgba(254, 62, 1, 0.2);
  flex-shrink: 0;
}

.paper-button {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  border: 1px solid rgba(254, 62, 1, 0.14);
  border-radius: 9999px;
  background: rgba(5, 5, 5, 0.84);
  color: #fff;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
}

.paper-button:hover {
  background: rgba(18, 9, 6, 0.92);
  border-color: rgba(254, 62, 1, 0.35);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 1px rgba(254, 62, 1, 0.08);
  transform: translateY(-1px);
}

.paper-button__brand-image {
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.paper-button__brand-image--arxiv {
  width: 3.5rem;
  height: 1.4rem;
}

.paper-button__brand-image--huggingface {
  width: 1.35rem;
  height: 1.35rem;
}

.paper-button__brand-image--github {
  width: 1.15rem;
  height: 1.15rem;
}

.example-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
  background: rgba(15, 15, 15, 0.82);
  color: rgba(255, 255, 255, 0.72);
  padding: 0.55rem 0.8rem;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.example-pill:hover {
  border-color: rgba(254, 62, 1, 0.2);
  color: rgba(255, 255, 255, 0.94);
  transform: translateY(-1px);
}

.example-pill--active {
  border-color: rgba(254, 62, 1, 0.28);
  background: rgba(254, 62, 1, 0.08);
  color: #fff;
}

.example-pill__domain {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.06);
  padding: 0.22rem 0.5rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #ffb18c;
}

.example-pill__title {
  font-size: 0.9rem;
  font-weight: 500;
}

.contributor-logo-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.98);
  overflow: hidden;
}

.contributor-logo-shell--empty {
  border-color: transparent;
  background: transparent;
}

.contributor-logo-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.contributor-logo-icon {
  font-size: 1rem;
  color: rgba(17, 24, 39, 0.78);
}

.instruction-shell {
  position: relative;
  overflow: hidden;
}

.instruction-shell--collapsed {
  max-height: 15rem;
}

.instruction-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.7;
  color: #d6cdc6;
}

.citation-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 15, 15, 0.9);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.03),
    0 16px 42px rgba(0, 0, 0, 0.2);
}

.citation-copy-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.06);
  color: #f5f1ed;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.citation-copy-button:hover {
  background: rgba(254, 62, 1, 0.12);
  border-color: rgba(254, 62, 1, 0.24);
  color: #ffb18c;
  transform: translateY(-1px);
}

.citation-code-shell {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.citation-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.94rem;
  line-height: 1.72;
  color: #f1ebe5;
}

.instruction-fade {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 5rem;
  background: linear-gradient(180deg, rgba(15, 15, 15, 0) 0%, rgba(15, 15, 15, 0.96) 80%);
  pointer-events: none;
}

.see-more-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  font-weight: 500;
  transition: color 0.2s ease;
}

.see-more-button:hover {
  color: #ff8a66;
}

.trajectory-step {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.02);
  padding: 1rem;
}

.trajectory-step__index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: 9999px;
  border: 1px solid rgba(254, 62, 1, 0.22);
  background: rgba(254, 62, 1, 0.12);
  color: #ffb18c;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.run-config-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.02);
  padding: 0.9rem 1rem;
}

.run-config-card__label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

.run-config-card__value {
  margin-top: 0.55rem;
  font-size: 0.92rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
}

.run-status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.run-status-chip--failed {
  border: 1px solid rgba(254, 62, 1, 0.2);
  background: rgba(254, 62, 1, 0.12);
  color: #ffb18c;
}

.run-status-chip--passed {
  border: 1px solid rgba(16, 185, 129, 0.2);
  background: rgba(16, 185, 129, 0.12);
  color: #86efac;
}

.run-criterion {
  border-radius: 1rem;
  padding: 1rem;
}

.run-criterion--failed {
  border: 1px solid rgba(254, 62, 1, 0.12);
  background: rgba(254, 62, 1, 0.04);
}

.run-criterion--passed {
  border: 1px solid rgba(16, 185, 129, 0.12);
  background: rgba(16, 185, 129, 0.05);
}

.tab-button {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  border-radius: 9999px;
  padding: 0.45rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease;
}

.tab-button--active {
  background: #fe3e01;
  color: #fff;
}

.accent-link:hover {
  color: #ff8a66;
  text-decoration-color: rgba(254, 62, 1, 0.4);
}
</style>
